import {
  DepositGroup,
  PayoutKind,
  TimelineDeposit,
  TimelineMerchant,
  TimelineMethod,
  TimelinePayout,
  TimelineResponse,
} from './response.types'
import { PayoutKindFilter, TimelineMode, TimelineView } from './types'

const PAYOUT_KINDS_BY_MODE: Record<TimelineMode, PayoutKind[]> = {
  uncovered: ['rejected'],
  covered: ['unmatched', 'matched_unpaid', 'paid', 'live'],
  all: ['rejected', 'unmatched', 'matched_unpaid', 'paid', 'live'],
}

const DEPOSIT_GROUPS_BY_MODE: Record<TimelineMode, DepositGroup[]> = {
  uncovered: ['no_payout', 'precheck_rejected'],
  covered: ['paid', 'unpaid', 'in_progress'],
  all: ['paid', 'unpaid', 'in_progress', 'no_payout', 'precheck_rejected'],
}

const kindFilterOf = (kind: PayoutKind): PayoutKindFilter =>
  kind === 'paid' || kind === 'live' ? 'other' : kind

const inSelection = (ids: number[], id: number) => ids.length === 0 || ids.includes(id)

/** Выплаты режима: фильтр видов действует только в режиме «Вместе». */
export const visiblePayouts = (data: TimelineResponse, view: TimelineView): TimelinePayout[] =>
  data.payouts.filter(
    (p) =>
      PAYOUT_KINDS_BY_MODE[view.mode].includes(p.kind) &&
      (view.mode !== 'all' || view.payoutKinds.includes(kindFilterOf(p.kind))) &&
      inSelection(view.payoutMerchantIds, p.merchant_id) &&
      inSelection(view.payoutMethodIds, p.payment_method_id)
  )

/**
 * Методы депозитов выбираются сами по себе. Пока их не выбрали, а методы выплат
 * выбраны, депозиты следуют за выплатами: проходит депозит, чей метод выбран или
 * чьи виды реквизита пересекаются с видами выбранного метода выплаты — так метод
 * выплаты с двойным реквизитом подтягивает депозиты обоих видов.
 */
export const visibleDeposits = (data: TimelineResponse, view: TimelineView): TimelineDeposit[] => {
  const kindsOf = new Map(data.payment_methods.map((m) => [m.id, m.requisite_kinds]))
  const payoutKinds = new Set(view.payoutMethodIds.flatMap((id) => kindsOf.get(id) ?? []))
  const methodPasses = (methodId: number) => {
    if (view.depositMethodIds.length > 0) return view.depositMethodIds.includes(methodId)
    if (view.payoutMethodIds.length === 0) return true
    return (
      view.payoutMethodIds.includes(methodId) ||
      (kindsOf.get(methodId) ?? []).some((k) => payoutKinds.has(k))
    )
  }

  return data.deposits.filter(
    (d) =>
      DEPOSIT_GROUPS_BY_MODE[view.mode].includes(d.group) &&
      inSelection(view.depositMerchantIds, d.merchant_id) &&
      methodPasses(d.payment_method_id)
  )
}

export type SideOptions = { merchants: TimelineMerchant[]; methods: TimelineMethod[] }

/** Опции фильтров стороны — только мерчанты и методы, которые на этой стороне есть в окне. */
export const sideOptions = (
  data: TimelineResponse | null,
  side: 'payouts' | 'deposits'
): SideOptions => {
  if (!data) return { merchants: [], methods: [] }
  const rows: { merchant_id: number; payment_method_id: number }[] =
    side === 'payouts' ? data.payouts : data.deposits
  const merchantIds = new Set(rows.map((r) => r.merchant_id))
  const methodIds = new Set(rows.map((r) => r.payment_method_id))
  return {
    merchants: data.merchants.filter((m) => merchantIds.has(m.id)),
    methods: data.payment_methods.filter((m) => methodIds.has(m.id)),
  }
}
